import React, { useEffect, useRef } from "react";
import SiriWave from "siriwave";

type VoiceWaveVisualizerProps = {
  isRecording: boolean;
  isPaused: boolean;
};

const VoiceWaveVisualizer: React.FC<VoiceWaveVisualizerProps> = ({
  isRecording,
  isPaused,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const waveRef = useRef<SiriWave | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    if (containerRef.current && !waveRef.current) {
      waveRef.current = new SiriWave({
        container: containerRef.current,
        width: containerRef.current.clientWidth,
        height: 33,
        style: "ios9",
        speed: 0.3,
        amplitude: 1.2,
        frequency: 6,
        autostart: false,
      });

      if (!isPaused) {
        waveRef.current.start();
      }
    }

    return () => {
      if (waveRef.current) {
        waveRef.current.dispose();
        waveRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (waveRef.current) {
      if (isPaused) {
        waveRef.current.stop();
      } else if (isRecording) {
        waveRef.current.start();
      }
    }
  }, [isPaused, isRecording]);

  useEffect(() => {
    const setupAudio = async () => {
      try {
        if (isRecording && !isPaused) {
          if (
            !audioContextRef.current ||
            audioContextRef.current.state === "closed"
          ) {
            audioContextRef.current = new (window.AudioContext ||
              (window as any).webkitAudioContext)();
          }
          analyserRef.current = audioContextRef.current.createAnalyser();
          analyserRef.current.fftSize = 256;

          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
          });
          streamRef.current = stream;

          const source =
            audioContextRef.current.createMediaStreamSource(stream);
          source.connect(analyserRef.current);

          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount
          );

          const updateWave = () => {
            if (
              !isPaused &&
              isRecording &&
              waveRef.current &&
              analyserRef.current
            ) {
              analyserRef.current.getByteFrequencyData(dataArray);
              const average =
                dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
              const amplitude = Math.max(0.1, Math.min(1.5, average / 128));
              waveRef.current.setAmplitude(amplitude * 15);
              animationFrameRef.current = requestAnimationFrame(updateWave);
            }
          };

          updateWave();
        } else {
          if (streamRef.current) {
            streamRef.current.getTracks().forEach((track) => track.stop());
            streamRef.current = null;
          }
          if (
            audioContextRef.current &&
            audioContextRef.current.state !== "closed"
          ) {
            audioContextRef.current.close();
            audioContextRef.current = null;
          }
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current);
            animationFrameRef.current = null;
          }
          if (waveRef.current) {
            waveRef.current.setAmplitude(0.1);
            if (!isRecording) {
              waveRef.current.stop();
            }
          }
        }
      } catch (error) {
        console.error("Error setting up audio:", error);
      }
    };

    setupAudio();

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close();
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isRecording, isPaused]);

  return (
    <div className="wave-container">
      <div ref={containerRef} />
      <style>{`
        .wave-container {
          width: 100%;
          height: 33px;
          background: transparent;
          position: relative;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default VoiceWaveVisualizer;
